import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { McValidators } from '@portal-core/forms/services/mc-validators';
import { DeleteConfirmDialogData } from '@portal-core/general/components/form/form.component';
import { ConnectionType } from '@portal-core/publications/enums/publication-connection-type.enum';
import { SftpAuthentication } from '@portal-core/publications/enums/sftp-authentication.enum';
import { SftpServerValidation } from '@portal-core/publications/enums/sftp-server-validation.enum';
import { ConnectorProperties } from '@portal-core/publications/models/connector-properties.model';
import { ConnectorSecrets } from '@portal-core/publications/models/connector-secrets.model';
import { ConnectorSettings } from '@portal-core/publications/models/connector-settings.model';
import { ConnectorUpdate } from '@portal-core/publications/models/connector-update.model';
import { Connector } from '@portal-core/publications/models/connector.model';
import { ConnectorService } from '@portal-core/publications/services/connector.service';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { Subscription } from 'rxjs';

export interface SftpForm {
  sftpUrl: FormControl<string>;
  sftpName: FormControl<string>;
  sftpAuthenticationType: FormControl<SftpAuthentication>;
  sftpUsername: FormControl<string>;
  sftpPassword: FormControl<string>;
  sftpPrivateKey: FormControl<string>;
  sftpPrivateKeyPassword: FormControl<string>;
  sftpValidationType: FormControl<SftpServerValidation>;
  sftpPublicKey: FormControl<string>;
}

interface TypeSelect<TValue extends string> {
  text: string;
  value: TValue;
}

type SftpControlName = keyof SftpForm;
type SecretControlName = 'sftpPassword' | 'sftpPrivateKey' | 'sftpPrivateKeyPassword';
type MaskedControlName = SecretControlName | 'sftpPublicKey';
type ManagedControlName = MaskedControlName;

interface ManagedControlState {
  element?: ElementRef;
  controlHidden?: boolean;
  valueHidden?: boolean;
}

@Component({
  selector: 'mc-sftp-form',
  templateUrl: './sftp-form.component.html',
  styleUrls: ['./sftp-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
@AutoUnsubscribe()
export class SftpFormComponent implements OnInit, AfterViewInit {
  @Input() connector: Connector;
  @Input() licenseId: number;
  @Input() licenseUserId: number = null;

  @Output() success = new EventEmitter<boolean>();
  @Output() cancel = new EventEmitter<boolean>();

  @ViewChild('sftpPassword', { static: false }) sftpPasswordElement: ElementRef;
  @ViewChild('sftpPrivateKey', { static: false }) sftpPrivateKeyElement: ElementRef;
  @ViewChild('sftpPrivateKeyPassword', { static: false }) sftpPrivateKeyPasswordElement: ElementRef;

  readonly authenticationTypes: TypeSelect<SftpAuthentication>[] = [
    { text: 'Password', value: SftpAuthentication.Password },
    { text: 'Private Key', value: SftpAuthentication.PrivateKey }
  ];

  readonly validationTypes: TypeSelect<SftpServerValidation>[] = [
    { text: 'None', value: SftpServerValidation.None },
    { text: 'Public Key', value: SftpServerValidation.PublicKey }
  ];

  readonly SftpAuthentication = SftpAuthentication;
  readonly SftpServerValidation = SftpServerValidation;

  managedControls: Record<ManagedControlName, ManagedControlState> = {
    sftpPassword: {
      controlHidden: false,
      valueHidden: true
    },
    sftpPrivateKey: {
      controlHidden: false,
      valueHidden: true
    },
    sftpPrivateKeyPassword: {
      controlHidden: false,
      valueHidden: true
    },
    sftpPublicKey: {
      valueHidden: true
    }
  };

  errorMessage: string;
  successMessage: string;
  deleteSuccessMessage: string;
  deleteConfirmDialogData: DeleteConfirmDialogData;

  sftpForm: FormGroup<SftpForm>;

  authenticationTypeSubscription: Subscription;
  validationTypeSubscription: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private connectorService: ConnectorService
  ) { }

  get controls(): SftpForm {
    return this.sftpForm.controls;
  }

  get authType(): SftpAuthentication {
    return this.controls.sftpAuthenticationType.value;
  }

  get validationType(): SftpServerValidation {
    return this.controls.sftpValidationType.value;
  }

  private get connectorSettings(): ConnectorSettings | null {
    return typeof this.connector?.Settings === 'object' ? this.connector.Settings : null;
  }

  ngOnInit() {
    this.buildForm();
    this.setupFormData();
    this.initializeManagedControls();
    this.syncFormState();

    this.authenticationTypeSubscription = this.controls.sftpAuthenticationType.valueChanges.subscribe(() => this.syncFormState());
    this.validationTypeSubscription = this.controls.sftpValidationType.valueChanges.subscribe(() => this.syncFormState());
  }

  ngAfterViewInit() {
    this.managedControls.sftpPassword.element = this.sftpPasswordElement;
    this.managedControls.sftpPrivateKey.element = this.sftpPrivateKeyElement;
    this.managedControls.sftpPrivateKeyPassword.element = this.sftpPrivateKeyPasswordElement;
  }

  private buildForm() {
    this.sftpForm = this.formBuilder.group({
      sftpName: new FormControl(this.connector?.Name ?? '', [Validators.required, Validators.maxLength(64)]),
      sftpUrl: new FormControl(this.connector?.Url ?? '', [Validators.required, McValidators.validURLName, Validators.maxLength(256)]),
      sftpAuthenticationType: new FormControl(this.connectorSettings?.Properties?.SftpAuthentication ?? SftpAuthentication.Password, [Validators.required]),
      sftpValidationType: new FormControl(this.connectorSettings?.Properties?.SftpServerValidation ?? SftpServerValidation.None, [Validators.required]),
      sftpUsername: new FormControl(this.connectorSettings?.Properties?.Username ?? '', [Validators.required]),
      sftpPassword: new FormControl('', [Validators.required]),
      sftpPrivateKey: new FormControl('', [Validators.required]),
      sftpPrivateKeyPassword: new FormControl('', []),
      sftpPublicKey: new FormControl(this.connectorSettings?.Properties?.SftpPublicKey ?? '', [Validators.required])
    });
  }

  private setupFormData() {
    if (this.connector) {
      this.errorMessage = 'Unable to update connector';
      this.successMessage = 'Connector updated';
    } else {
      this.errorMessage = 'Unable to create connector';
      this.successMessage = 'Connector created';
    }

    this.deleteSuccessMessage = 'Connector deleted';
    this.deleteConfirmDialogData = {
      title: 'Delete Connector',
      prompt: `Are you sure you want to delete '${this.connector?.Name}'?`
    };
  }

  private initializeManagedControls() {
    const authType = this.connectorSettings?.Properties?.SftpAuthentication;

    if (authType === SftpAuthentication.PrivateKey) {
      this.managedControls.sftpPrivateKey.controlHidden = true;
      this.managedControls.sftpPrivateKeyPassword.controlHidden = true;
    } else if (authType === SftpAuthentication.Password) {
      this.managedControls.sftpPassword.controlHidden = true;
    }
  }

  private syncFormState() {
    this.setEnabled('sftpPassword', this.authType === SftpAuthentication.Password && !this.isControlHidden('sftpPassword'));
    this.setEnabled('sftpPrivateKey', this.authType === SftpAuthentication.PrivateKey && !this.isControlHidden('sftpPrivateKey'));
    this.setEnabled('sftpPrivateKeyPassword', this.authType === SftpAuthentication.PrivateKey && !this.isControlHidden('sftpPrivateKeyPassword'));
    this.setEnabled('sftpPublicKey', this.validationType === SftpServerValidation.PublicKey);
  }

  private setEnabled(name: SftpControlName, enabled: boolean) {
    const control = this.controls[name];

    if (enabled) {
      control.enable({ emitEvent: false });
    } else {
      control.disable({ emitEvent: false });
    }
  }

  private isControlHidden(name: SecretControlName): boolean {
    return this.managedControls[name].controlHidden === true;
  }

  showSecretControl(name: SecretControlName) {
    this.managedControls[name].controlHidden = false;

    const element = this.managedControls[name].element;
    if (element) {
      requestAnimationFrame(() => element.nativeElement.scrollIntoView(true));
    }

    this.syncFormState();
  }

  toggleValueHidden(name: MaskedControlName) {
    this.managedControls[name].valueHidden = !this.managedControls[name].valueHidden;
  }

  deleteFn = () => {
    return this.connectorService.deleteConnectors$([this.connector.Id]);
  }

  submitFn = (form: FormGroup<SftpForm>) => {
    const connectorUpdate: Partial<ConnectorUpdate> = {
      Id: this.connector?.Id,
      Url: form.controls.sftpUrl.value,
      Name: form.controls.sftpName.value,
      ConnectionType: ConnectionType.SFTP,
      LicenseId: this.licenseId,
      LicenseUserId: this.licenseUserId,
      Settings: JSON.stringify(this.buildSettings(form))
    };

    if (this.connector) {
      return this.connectorService.updateConnector$(connectorUpdate);
    }

    return this.connectorService.createConnector$(connectorUpdate);
  }

  private buildSettings(form: FormGroup<SftpForm>): Partial<ConnectorSettings> {
    const authenticationType = form.controls.sftpAuthenticationType.value;
    const validationType = form.controls.sftpValidationType.value;

    const properties: Partial<ConnectorProperties> = {
      SftpAuthentication: authenticationType,
      SftpServerValidation: validationType
    };

    const secrets: Partial<ConnectorSecrets> = {};

    // validation
    switch (validationType) {
      case SftpServerValidation.PublicKey:
        properties.SftpPublicKey = form.controls.sftpPublicKey.value;
        break;

      case SftpServerValidation.None:
        properties.SftpPublicKey = null;
        break;
    }

    // authentication
    properties.Username = form.controls.sftpUsername.value;

    switch (authenticationType) {
      case SftpAuthentication.Password:
        // only include secrets if the control is visible
        if (!this.isControlHidden('sftpPassword')) {
          secrets.Password = form.controls.sftpPassword.value;
        }

        // clear other authentication type settings
        secrets.SftpPrivateKey = null;
        secrets.SftpPrivateKeyPassword = null;
        break;

      case SftpAuthentication.PrivateKey:
        // only include secrets if the control is visible
        if (!this.isControlHidden('sftpPrivateKey')) {
          secrets.SftpPrivateKey = form.controls.sftpPrivateKey.value;
        }

        if (!this.isControlHidden('sftpPrivateKeyPassword')) {
          secrets.SftpPrivateKeyPassword = form.controls.sftpPrivateKeyPassword.value;
        }

        // clear other authentication type settings
        secrets.Password = null;
        break;
    }

    return {
      Properties: properties,
      Secrets: secrets
    };
  }
}
